class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      // Fix (Oct 2026): the old text may not be in the button (we send Shopify a cleaned-up search, e.g. 'mfc-jer'
      // goes as 'mfc jer'), and characters like ( or + broke the pattern. Either crashed here, so no new results
      // were fetched and the old ones stayed on screen.
      const escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const found = currentButtonText.match(new RegExp(escaped, 'g'));
      if (!found || found.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // Suggestions only (Oct 2026): no plain search-phrase suggestions, they link to the /search results page.
    // Exact match (Oct 2026): filler words ('ink', 'cartridges', 'for'...) are left out of what is sent to Shopify,
    // otherwise "hp deskjet 2720e ink cartridges" doesn't find the printer page. Up to 10 of each kind come back.
    // If a model number was typed without its dash ("mfcl9570cdwt"), also ask with the dash put back as a space
    // ("mfc l9570cdwt") - Shopify can't find the joined-up form on its own. Both run together; the one with more
    // exact matches wins (the typed form wins a tie, so codes like Konica "TN514" that have no dash still work).
    const mitQuery = this.mitWords(searchTerm).join(' ') || searchTerm;
    const mitFetch = (q) =>
      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=10&resources[limit_scope]=each`, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) =>
          this.mitExactMatch(
            new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search')
              .innerHTML,
            searchTerm
          )
        );
    // Also the model pieces joined back up ("tn5-14" -> "tn514", "ts 5350" -> "ts5350"), see mitJoinWords.
    const mitTries = [mitQuery];
    [this.mitDashQuery(searchTerm), this.mitJoinWords(searchTerm).join(' ')].forEach((q) => {
      if (q && mitTries.indexOf(q) === -1) mitTries.push(q);
    });
    const mitBoth = Promise.all(
      mitTries.map((q, i) => (i === 0 ? mitFetch(q) : mitFetch(q).catch(() => ({ kept: -1 }))))
    ).then((results) => results.reduce((best, r) => (r.kept > best.kept ? r : best)));
    mitBoth
      .then((result) => {
        const resultsMarkup = result.markup;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  // ---- Exact match (Oct 2026) ----
  // Shopify's search is loose: "canon ts8152" showed a Canon CL-546XL and a Konica pack (code A9E8150).
  // A result now stays only if EVERY word typed is found in its title, or (products) in its compatible
  // printer list (data-mit-match, from custom.compatible_printer_collections). Filler words are ignored.
  // Words match the start of a word in the title; spaces and dashes don't matter ("tn-2420" = "tn2420",
  // "ts 8140" = "ts8140"). A number must be the whole number: "305" matches 305XL but not DeskJet 3050.
  // If that leaves nothing, the last word is treated as still being typed ("hp 30" shows 301, 302, 305...).
  mitWords(term) {
    const filler = ['for', 'the', 'and', 'with', 'a', 'an', 'of', 'to', 'my', 'in', 'printer', 'printers',
      'cartridge', 'cartridges', 'ink', 'inks', 'toner', 'toners'];
    // Common brand/range misspellings (Oct 2026). Brand and range names only - model numbers are never guessed.
    const fixes = {
      cannon: 'canon', cannan: 'canon', canan: 'canon', canonn: 'canon',
      epsom: 'epson', espon: 'epson', epsn: 'epson', epsen: 'epson', esspon: 'epson',
      brothers: 'brother', borther: 'brother', brohter: 'brother', brother1: 'brother', bruther: 'brother',
      kyocerra: 'kyocera', kyosera: 'kyocera', kiocera: 'kyocera', kycera: 'kyocera', kyocra: 'kyocera',
      samsun: 'samsung', samung: 'samsung', sumsung: 'samsung', samsang: 'samsung',
      lexmarc: 'lexmark', lexmar: 'lexmark', lexmart: 'lexmark',
      zerox: 'xerox', xerux: 'xerox',
      konika: 'konica', konnica: 'konica', minolto: 'minolta',
      hewlett: 'hp', packard: '', hewlettpackard: 'hp',
      pixima: 'pixma', pixmax: 'pixma', pxima: 'pixma',
      deskjett: 'deskjet', dekjet: 'deskjet', officejett: 'officejet', laserjett: 'laserjet',
    };
    const t = term
      .toLowerCase()
      .replace(/\b(desk|office|laser)[\s-]+jet\b/g, '$1jet');
    return t
      .split(/[^a-z0-9]+/)
      .map((w) => (Object.prototype.hasOwnProperty.call(fixes, w) ? fixes[w] : w))
      .filter((w) => w && filler.indexOf(w) === -1);
  }

  // One-name matching (Oct 2026): a product's text is "title | printer 1 | printer 2 ...". All the words must be
  // found in the title, or in the title plus ONE printer - never spread over two different printers (otherwise
  // "deskjet 2720" could pass a cartridge listing "DeskJet 2320" and "Envy 2720"). Returns one token set per option.
  mitTokens(text) {
    const names = (text || '').split('|').map((name) => this.mitNameTokens(name));
    const title = names[0] || [];
    if (names.length < 2) return [title];
    return [title].concat(names.slice(1).map((printer) => title.concat(printer)));
  }

  mitNameTokens(name) {
    const tokens = [];
    [name.toLowerCase()].forEach((name) => {
      const parts = name.split(/[^a-z0-9]+/).filter(Boolean);
      parts.forEach((part, n) => {
        tokens.push(part);
        const pieces = part.match(/[a-z]+|[0-9]+/g) || [];
        // every tail of the word too, so "ts8140" also gives "8140" and "l2350dw" gives "2350dw"
        for (let i = 1; i < pieces.length; i++) tokens.push(pieces.slice(i).join(''));
        // the word joined to the next one or two, so "MFC-L9570CDWT" also gives "mfcl9570cdwt"
        if (parts[n + 1]) tokens.push(part + parts[n + 1]);
        if (parts[n + 2]) tokens.push(part + parts[n + 1] + parts[n + 2]);
      });
    });
    return tokens;
  }

  // Model pieces typed with a space or dash in an odd place, joined back up: "tn5-14" -> "tn514",
  // "ts 5350" -> "ts5350", "t n514" -> "tn514", "305 xl" -> "305xl". Brand names are never joined ("hp 305" stays).
  mitJoinWords(term) {
    const brands = ['hp', 'oki', 'dell', 'ibm', 'lg'];
    const out = [];
    this.mitWords(term).forEach((w) => {
      const last = out[out.length - 1];
      const join =
        last !== undefined &&
        brands.indexOf(last) === -1 &&
        (/^[a-z]$/.test(last) ||
          (/^[a-z]{1,3}$/.test(last) && /^[0-9]/.test(w)) ||
          (/[0-9]$/.test(last) && /^[0-9]/.test(w)) ||
          (/[0-9]$/.test(last) && /^[a-z]{1,3}$/.test(w)));
      if (join) out[out.length - 1] = last + w;
      else out.push(w);
    });
    return out;
  }

  // Model numbers that have a dash in our titles: put it back when a customer leaves it out.
  // Longest prefixes first (pgi before pg, clp before cl). Only words containing a digit are touched.
  mitDashQuery(term) {
    const prefixes = ['pgi', 'cli', 'mfc', 'dcp', 'fax', 'clp', 'clx', 'clt', 'mlt', 'scx',
      'hl', 'tn', 'dr', 'lc', 'bu', 'wt', 'pg', 'cl', 'tk', 'fs', 'wf', 'xp', 'et', 'ml', 'sl'];
    let changed = false;
    const words = this.mitWords(term).map((w) => {
      if (!/[0-9]/.test(w)) return w;
      const p = prefixes.find((x) => w.indexOf(x) === 0 && w.length > x.length + 1);
      if (!p) return w;
      changed = true;
      return p + ' ' + w.slice(p.length);
    });
    return changed ? words.join(' ') : null;
  }

  mitWordFound(word, tokens, loose) {
    const endsInDigit = /[0-9]$/.test(word);
    return tokens.some((t) => {
      if (t.indexOf(word) !== 0) return false;
      if (loose || !endsInDigit || t.length === word.length) return true;
      return !/[0-9]/.test(t.charAt(word.length));
    });
  }

  mitExactMatch(markup, term) {
    const words = this.mitWords(term);
    if (!words.length) return { markup, kept: -1 };
    // a result passes with the words as typed OR with the model pieces joined up (mitJoinWords)
    const joined = this.mitJoinWords(term);
    const wordLists = joined.join(' ') === words.join(' ') ? [words] : [words, joined];

    const box = document.createElement('div');
    box.innerHTML = markup;
    const items = Array.from(box.querySelectorAll('li[role="option"]')).map((li) => {
      const heading = li.querySelector('.predictive-search__item-heading');
      const text = li.getAttribute('data-mit-match') || (heading ? heading.textContent : li.textContent);
      return { li, sets: this.mitTokens(text) };
    });
    const keeps = (item, loose) =>
      wordLists.some((list) =>
        item.sets.some((tokens) =>
          list.every((w, i) => this.mitWordFound(w, tokens, loose && i === list.length - 1))
        )
      );
    let kept = items.filter((item) => keeps(item, false));
    if (!kept.length) kept = items.filter((item) => keeps(item, true));
    items.forEach((item) => {
      if (kept.indexOf(item) === -1) item.li.remove();
    });

    box.querySelectorAll('ul.predictive-search__results-list').forEach((ul) => {
      if (!ul.querySelector('li') && ul.parentElement) ul.parentElement.remove();
    });
    box.querySelectorAll('.predictive-search__result-group').forEach((group) => {
      if (!group.querySelector('li')) group.remove();
    });

    const count = box.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) count.setAttribute('data-total-results', kept.length);
    if (!kept.length) {
      const wrapper = box.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.remove();
      const none = document.createElement('div');
      none.className = 'mit-ps-none';
      none.innerHTML = 'No exact match for <strong></strong>. Please check the model number on your printer, or <a href="/pages/contact-us">contact us</a> and we’ll find the right cartridge for you.';
      none.querySelector('strong').textContent = '“' + term + '”';
      const root = box.querySelector('#predictive-search-results') || box;
      root.insertBefore(none, root.firstChild);
      if (count) count.textContent = 'No exact match for ' + term;
    }
    return { markup: box.innerHTML, kept: kept.length };
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    // Fix: on tablet widths (750-989px) the header can wrap into a taller,
    // multi-row layout. If that ever pushes the computed available space to
    // near-zero or negative, the results panel would collapse to just its
    // border (a thin line) instead of showing the results. Clamp to a
    // sensible minimum so the dropdown is always usable.
    this.resultsMaxHeight = Math.max(200, window.innerHeight - headerBottom);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
